import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import {
  subscribeToSeasonTimes,
  createSeasonTime,
  updateSeasonTime,
  deleteSeasonTime,
  type SeasonTime,
} from '../../services/seasonTime.service';
import { getAllCropTypes } from '../../services/cropType.service';
import { getAllRegions } from '../../services/region.service';
import { toastSuccess, toastError } from '../../components/core';
import { Navbar } from '../../components/Navbar';
import {
  BackButton,
  Button,
  Badge,
  ConfirmDialog,
  EmptyState,
  FAB,
  Spinner,
} from '../../components/core';
import { Icon, PlusIcon } from '../../components/core';
import { SeasonTimesForm } from './SeasonTimesForm';
import { SeasonTimesTable } from './SeasonTimesTable';
import { SeasonTimeCards } from './SeasonTimeCards';
import { type FormFields, type FormErrors, EMPTY_FIELDS } from './types';

export default function SeasonTimesPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // List state
  const [seasonTimes, setSeasonTimes] = useState<SeasonTime[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Reference data
  const [cropTypes, setCropTypes] = useState<{ id: string; name: string }[]>([]);
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);

  // Form state (shared between add and edit)
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fields, setFields] = useState<FormFields>(EMPTY_FIELDS);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Subscribe to season times ────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeToSeasonTimes(
      orgId,
      data => {
        setSeasonTimes(data);
        setLoading(false);
      },
      () => {
        setListError(t('seasonTimes.loadError'));
        setLoading(false);
      },
    );
    return unsub;
  }, [orgId, t]);

  // ─── Load reference data ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [cts, regs] = await Promise.all([getAllCropTypes(orgId), getAllRegions(orgId)]);
        setCropTypes(cts);
        setRegions(regs);
      } catch (err) {
        console.error('[SeasonTimesPage] load reference data error:', err);
        // Non-critical — selects will show empty options
      }
    })();
  }, [orgId]);

  // ─── Lookup helpers ───────────────────────────────────────────────────────
  function cropName(id: string) {
    return cropTypes.find(c => c.id === id)?.name ?? id;
  }

  function regionName(id: string) {
    return regions.find(r => r.id === id)?.name ?? id;
  }

  // ─── Form helpers ─────────────────────────────────────────────────────────
  function openAdd() {
    setFields(EMPTY_FIELDS);
    setErrors({});
    setEditingId(null);
    setFormMode('add');
  }

  function openEdit(st: SeasonTime) {
    setFields({
      cropTypeId: st.cropTypeId,
      regionId: st.regionId,
      startMonth: st.startMonth,
      startDay: st.startDay,
      endMonth: st.endMonth,
      endDay: st.endDay,
    });
    setErrors({});
    setEditingId(st.id);
    setFormMode('edit');
  }

  function closeForm() {
    setFormMode(null);
    setEditingId(null);
    setFields(EMPTY_FIELDS);
    setErrors({});
  }

  function handleSelectChange(e: React.ChangeEvent<{ name?: string; value: string }>) {
    const { name, value } = e.target;
    if (!name) return;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  function handleStartChange(month: number | '', day: number | '') {
    setFields(prev => ({ ...prev, startMonth: month, startDay: day }));
    if (errors.startDate) setErrors(prev => ({ ...prev, startDate: undefined }));
  }

  function handleEndChange(month: number | '', day: number | '') {
    setFields(prev => ({ ...prev, endMonth: month, endDay: day }));
    if (errors.endDate) setErrors(prev => ({ ...prev, endDate: undefined }));
  }

  function validateForm(): FormErrors {
    const errs: FormErrors = {};
    if (!fields.cropTypeId) errs.cropTypeId = t('seasonTimes.cropTypeRequired');
    if (!fields.regionId) errs.regionId = t('seasonTimes.regionRequired');
    if (fields.startMonth === '' || fields.startDay === '') errs.startDate = t('seasonTimes.startDateRequired');
    if (fields.endMonth === '' || fields.endDay === '') errs.endDate = t('seasonTimes.endDateRequired');
    return errs;
  }

  // ─── Save handler ─────────────────────────────────────────────────────────
  async function handleSave() {
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      if (formMode === 'add') {
        await createSeasonTime(orgId, {
          cropTypeId: fields.cropTypeId,
          regionId: fields.regionId,
          startMonth: fields.startMonth as number,
          startDay: fields.startDay as number,
          endMonth: fields.endMonth as number,
          endDay: fields.endDay as number,
        });
        toastSuccess(t('seasonTimes.toastCreated'));
      } else if (formMode === 'edit' && editingId) {
        await updateSeasonTime(orgId, editingId, {
          startMonth: fields.startMonth as number,
          startDay: fields.startDay as number,
          endMonth: fields.endMonth as number,
          endDay: fields.endDay as number,
        });
        toastSuccess(t('seasonTimes.toastUpdated'));
      }
      closeForm();
    } catch (err: unknown) {
      console.error('[SeasonTimesPage] save error:', err);
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : '';
      if (code === 'already-exists') {
        setErrors({ general: t('seasonTimes.alreadyExists') });
      } else {
        setErrors({ general: t('seasonTimes.saveError') });
      }
    } finally {
      setSaving(false);
    }
  }

  // ─── Delete handler ───────────────────────────────────────────────────────
  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSeasonTime(orgId, deleteTarget.id);
      toastSuccess(t('seasonTimes.toastDeleted'));
      setDeleteTarget(null);
    } catch (err) {
      console.error('[SeasonTimesPage] delete error:', err);
      toastError(t('seasonTimes.deleteError'));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-3xl px-4 py-6 pb-24 sm:px-6 sm:pb-6">
        <BackButton />

        {/* Page heading */}
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-xl font-semibold text-gray-900">
              {t('seasonTimes.title')}
              {!loading && <Badge>{seasonTimes.length}</Badge>}
            </h1>
            <p className="mt-1 font-body text-sm text-gray-500">{t('seasonTimes.subtitle')}</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<Icon size="sm"><PlusIcon /></Icon>}
            onClick={openAdd}
            className="hidden shrink-0 sm:inline-flex"
          >
            {t('seasonTimes.add')}
          </Button>
        </div>

        {/* Add / Edit form */}
        {formMode !== null && (
          <SeasonTimesForm
            mode={formMode}
            fields={fields}
            errors={errors}
            cropTypes={cropTypes}
            regions={regions}
            saving={saving}
            onSelectChange={handleSelectChange}
            onStartChange={handleStartChange}
            onEndChange={handleEndChange}
            onSave={handleSave}
            onCancel={closeForm}
          />
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : listError ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {listError}
          </div>
        ) : seasonTimes.length === 0 && formMode === null ? (
          <EmptyState
            emoji="📅"
            title={t('seasonTimes.empty')}
            description={t('seasonTimes.emptyHelp')}
            action={
              <Button
                variant="primary"
                size="sm"
                icon={<Icon size="sm"><PlusIcon /></Icon>}
                onClick={openAdd}
              >
                {t('seasonTimes.add')}
              </Button>
            }
          />
        ) : seasonTimes.length > 0 ? (
          (() => {
            const sorted = [...seasonTimes].sort((a, b) => {
              const cmp = cropName(a.cropTypeId).localeCompare(cropName(b.cropTypeId));
              if (cmp !== 0) return cmp;
              return regionName(a.regionId).localeCompare(regionName(b.regionId));
            });
            return (
              <>
                <SeasonTimesTable
                  seasonTimes={sorted}
                  editingId={editingId}
                  cropName={cropName}
                  regionName={regionName}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
                <SeasonTimeCards
                  seasonTimes={sorted}
                  editingId={editingId}
                  cropName={cropName}
                  regionName={regionName}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
              </>
            );
          })()
        ) : null}
      </main>

      <FAB ariaLabel={t('seasonTimes.add')} onClick={openAdd} />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('seasonTimes.deleteTitle')}
        message={
          <>
            {t('seasonTimes.deleteMessagePre')}{' '}
            <strong>{deleteTarget?.label}</strong>
            {t('seasonTimes.deleteMessagePost')}
          </>
        }
        confirmLabel={t('seasonTimes.deleteConfirm')}
        loadingLabel={t('seasonTimes.deleting')}
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
